import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  useLazyGetPaginatedUsersQuery,
  useUpdateUserArchiveMutation,
} from 'app/services/user.service'

import { FixMeLater } from 'types'
import { IUser } from 'types/user.types'
import colors from 'utils/colors'
import useInfoModal from 'utils/hooks/useInfoModal'
import { BUTTONS, ERRORS, FORM, INFORMATION, MODALS, PLACEHOLDERS, ROLES, TABLE } from 'locales/locales'

import CustomButton from 'components/button/CustomButton'
import Loading from 'components/spinner/Loading'

import KGrid from 'components/kendo/grid/KGrid'
import { GridColumn } from '@progress/kendo-react-grid'
import { FiUser, FiKey } from "react-icons/fi";
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, UserManagementPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import CreateUserModal from './user-create/CreateUserModal'
import UserInfoModal from './user-info/UserInfoModal'
import { icons } from 'utils/icons'
import { DROPDOWN } from '../../../../locales/locales';
import CustomIcon from 'components/icon/CustomIcon'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import CustomChipLable from 'components/chip/ChipLable'
import axiosApi from 'app/axiosApi'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { SiMicrosoftexcel } from "react-icons/si";

interface DataStateFilter {
  field: string;
  operator: string;
  value: boolean | string;
  logic?: string;
  filters?: DataStateFilter[];
}

interface DataState {
  take: number;
  skip: number;
  filter: {
    logic: string;
    filters: DataStateFilter[];
  };
  sort?: Array<{ field: string; dir: string }>;
}

const initialDataState: DataState = {
  take: 10,
  skip: 0,
  filter: {
    logic: 'and',
    filters: [],
  },
  sort: []
}





const AdminUserManagement = () => {
  const { t } = useTranslation()


  const downloadSampleExcel = useCallback(async () => {
    // xlsx paketi yoksa: npm i xlsx
    const XLSX = await import("xlsx");
  
    const rows = [
      {
        username: "user.name",
        email: "user.email@test.com",
        password: "testpassword",
        title: "test department",
        fullname: "test user",
      }
    ];
  
    // Header sırası sabit kalsın diye header veriyorum
    const header = ["username", "email", "password", "title", "fullname"];
    const ws = XLSX.utils.json_to_sheet(rows, { header });
  
    // İstersen kolon genişlikleri
    ws["!cols"] = [
      { wch: 18 }, // username
      { wch: 28 }, // email
      { wch: 14 }, // password
      { wch: 14 }, // title
      { wch: 20 }, // fullname
    ];
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "users");
  
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users-import-sample.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const dropDownOptions = useMemo(() => [
    {
      title: t(DROPDOWN.ACTIVE_USERS),
      value: JSON.stringify({ field: 'isDeleted', operator: 'eq', value: false }),
    },
    {
      title: t(DROPDOWN.DEACTIVE_USERS),
      value: JSON.stringify({ field: 'isDeleted', operator: 'eq', value: true }),
    },
  ], [t])

  const [userId, setUserId] = useState('')
  const { openModal, closeModal, Modal } = useInfoModal()
  const [currentData, setCurrentData] = useState({ data: [] as FixMeLater[], totalCount: 1 })
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false)
  const [initialTabIndex, setInitialTabIndex] = useState(0)
  const [activeUsersFilter, setActiveUsersFilter] = useState(dropDownOptions[0])
  const [isLoading, setIsLoading] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null)
  const [getPaginatedUsers] = useLazyGetPaginatedUsersQuery()
  const [updateUserArchiveById] = useUpdateUserArchiveMutation()
  const [dataState, setDataState] = useState<DataState>({
    ...initialDataState,
    filter: { ...initialDataState.filter, filters: [JSON.parse(activeUsersFilter.value)] },
  })
  
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasUserUpdatePermission = useMemo(() => isFlagSetIn(
    [UserManagementPermissions.UPDATE_USER],
    permissions.userManagement,
  ), [permissions.userManagement])
  
  const hasUserCreateAndArchivePermission = useMemo(() => isFlagSetIn(
    [UserManagementPermissions.CREATE_AND_ARCHIVE_USER],
    permissions.userManagement,
  ), [permissions.userManagement])

  useEffect(() => {
    fetchUsers()
  }, [dataState])

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await getPaginatedUsers({
        ...dataState,
        count: true,
      })

      const formattedUsers = data?.data?.users.map((user: IUser) => ({
        ...user,
        actions: {
          actionKey: user?.isDeleted
            ? { text: t(TABLE.USER_ACTIVE), color: colors.vime, icon: icons.unarchiveNonFilledIcon }
            : { text: t(TABLE.USER_DEACTIVE), color: colors.red, icon: icons.archiveIcon },
        },
      }))

      if (data.data) setCurrentData({ 
        data: formattedUsers, 
        totalCount: data.data?.totalCount || 0 
      })
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }, [dataState, getPaginatedUsers, t])

  const onToggleArchive = useCallback((user: IUser, index: number) => {
    if (currentData?.data?.[index]) {
      const { isDeleted } = currentData.data[index]
      openModal({
        title: isDeleted ? t(MODALS.ACTIVE_USER_TITLE) : t(MODALS.INACTIVE_USER_TITLE),
        description: isDeleted
          ? t(MODALS.ACTIVE_USER_DESCRIPTION)
          : t(MODALS.INACTIVE_USER_DESCRIPTION),
        cancelTitle: t(BUTTONS.CANCEL) as string,
        okTitle: t(BUTTONS.YES) as string,
        onCancel: closeModal,
        onOk: () => onConfirmArchiveUser(user, !isDeleted),
      })
    }
  }, [currentData, t, openModal, closeModal])

  const onConfirmArchiveUser = useCallback(async (user: IUser, isDeleted: boolean) => {
    try {
      Loading.ref.show()
      await updateUserArchiveById({ userId: user._id, isDeleted })
      fetchUsers()
    } finally {
      closeModal()
      Loading.ref.hide()
    }
  }, [updateUserArchiveById, fetchUsers, closeModal])

  const onEdit = useCallback((user: IUser, tabIndex?: number) => {
    setUserId(user._id)
    setIsInfoModalVisible(true)
    setInitialTabIndex(tabIndex || 0)
  }, [])

  const RolesCell = (props: FixMeLater) => {
    const {
      dataItem: { roles = [] },
    } = props

    const maxVisible = 8 
    const visibleRoles = roles.slice(0, maxVisible)
    const hiddenCount = roles.length - maxVisible
    const tooltipText = roles.slice(maxVisible).join(', ')

    const content = (
      <RolesContainer>
        {visibleRoles.map((role: string, index: number) => (
          <RoleTag key={index}>
            {role}
          </RoleTag>
        ))}
        {hiddenCount > 0 && (
          <RoleTag title={tooltipText}>
            +{hiddenCount} {t(ROLES.OTHER)}
          </RoleTag>
        )}
      </RolesContainer>
    )

    return (
      <td>
        <Tooltip
          content={content}
          position="bottom"
          openDelay={100}
          parentTitle={true}
          anchorElement="target"
          style={{
            maxWidth: '200px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            fontSize: '10px',
          }}
        />
      </td>
    )
  }

  const ActionCell = useCallback(
    ({ dataItem, dataIndex }: FixMeLater) => (
      <td>
      <ButtonContainer>
    
        {hasUserUpdatePermission && (
          <Tooltip
            content={
              <StyledCustomIcon title={t(MODALS.USER_INFO_TITLE)}>
                <FiUser onClick={() => onEdit(dataItem)} size={23} />
              </StyledCustomIcon>
            }
            position="top"
            parentTitle={true}
            anchorElement="target"
            openDelay={100}
          />
        )}
    
        {/* PASSWORD CHANGE BUTTON */}
        {hasUserUpdatePermission && (
          <Tooltip
            content={
              <StyledCustomIcon title={t(BUTTONS.CHANGE_PASSWORD)}>
                <FiKey
                  size={21}
                  onClick={() => {
                    // Open modal with password tab - UserInfoModal will calculate the correct index
                    onEdit(dataItem, -1) // -1 means "open password tab"
                  }}
                />
              </StyledCustomIcon>
            }
            position="top"
            parentTitle={true}
            anchorElement="target"
            openDelay={100}
          />
        )}
    
        {hasUserCreateAndArchivePermission && (
          <Tooltip
            content={
              <StyledCustomIcon title={t(dataItem.actions.actionKey.text)}>
                <CustomIcon
                  iconPreset="grande"
                  source={dataItem.actions.actionKey.icon}
                  onClick={() => {
                    onToggleArchive(dataItem, dataIndex);
                  }}
                  alt="delete"
                />
              </StyledCustomIcon>
            }
            position="top"
            parentTitle={true}
            anchorElement="target"
          />
        )}
    
      </ButtonContainer>
    </td>
    
    ),
    [hasUserCreateAndArchivePermission, hasUserUpdatePermission, onToggleArchive, onEdit],
  )

  const handleFilterChange = useCallback((event: FixMeLater) => {
    setDataState({
      ...dataState,
      filter: event.filter,
    })
  }, [dataState])

  const onDataStateChange = useCallback((event: FixMeLater) => {
    setDataState({
      ...dataState,
      skip: event.dataState.skip,
      take: event.dataState.take,
      sort: event.dataState.sort || [],
    })
  }, [dataState])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    if (searchValue?.length > 2) {
      setDataState({
        ...dataState,
        filter: {
          logic: 'and',
          filters: [
            JSON.parse(activeUsersFilter.value),
            {
              logic: 'or',
              filters: [
                { field: 'fullname', operator: 'contains', value: searchValue as any },
                { field: 'email', operator: 'contains', value: searchValue as any },
              ],
            } as any,
          ],
        },
        skip: 0,
      })
    }else {
      setDataState({
        ...dataState,
        filter: {
          logic: 'and',
          filters: [JSON.parse(activeUsersFilter.value)],
        },
        skip: 0,
      })
    }
  }, [dataState])

  const handleDropdownChange = useCallback((e: FixMeLater) => {
    setActiveUsersFilter({
      title: JSON.parse(e.value).value
        ? dropDownOptions[1].title
        : dropDownOptions[0].title,
      value: e.value,
    })

    setDataState({
      ...dataState,
      filter: { ...dataState.filter, filters: [JSON.parse(e.value)] },
      take: 10,
      skip: 0,
    })
  }, [dataState, dropDownOptions])

  const handleCreateModalClose = useCallback(() => {
    setIsCreateModalVisible(false)
    fetchUsers()
  }, [fetchUsers])

  const handleInfoModalClose = useCallback(() => {
    setIsInfoModalVisible(false)
  }, [])

  const handleReloadClick = useCallback(() => {
    setIsLoading(true)
    fetchUsers()
  }, [fetchUsers])

  const handleImportUsersClick = useCallback(() => {
    if (fileInputRef) {
      fileInputRef.click()
    }
  }, [fileInputRef])

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const file = files[0]
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ]

    if (!allowedTypes.includes(file.type)) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.INVALID_EXCEL_FILE),
      })
      return
    }

    setIsImporting(true)
    try {
      const url = 'api/migration/import-users';
      const filesArray = Array.from(files)
      const params = {}
      
      const result = await axiosApi.uploadFile(url, filesArray, 'file', { params: params }, undefined)
      
      if (result.data?.success) {
        DropdownAlert.ref.show({
          isError: false,
          message: t(INFORMATION.USERS_IMPORTED_SUCCESS),
        })
        fetchUsers()
      } else {
        DropdownAlert.ref.show({
          isError: true,
          message: result.data?.message || t(ERRORS.USERS_IMPORT_ERROR),
        })
      }
    } catch (error: any) {
      console.error('Import users error:', error)
      DropdownAlert.ref.show({
        isError: true,
        message: error?.response?.data?.message || t(ERRORS.USERS_IMPORT_ERROR),
      })
    } finally {
      setIsImporting(false)
      // Reset file input
      if (fileInputRef) {
        fileInputRef.value = ''
      }
    }
  }, [fileInputRef, fetchUsers])

  return (
    <Container>
      <Wrapper>
        <ContentContainer>
          <HeaderBox>
            <HeaderTitle>{t(BUTTONS.USERS)}</HeaderTitle>
            <HeaderActions>
              {hasUserCreateAndArchivePermission && (
                <>
                  <input
                    ref={(ref) => setFileInputRef(ref)}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <ImportButtonGroup>
                    <AddButton
                      text={isImporting ? t(BUTTONS.LOADING) : t(BUTTONS.IMPORT_USERS_BUTTON)}
                      textColor="white"
                      onClick={handleImportUsersClick}
                      disabled={isImporting}
                    />
                    <Tooltip
                      content={
                        <StyledCustomIcon
                          title={t(INFORMATION.EXCEL_ONLY_SUPPORTED)}
                          onClick={downloadSampleExcel}
                        >
                          <SiMicrosoftexcel size={18} color="#1D6F42" />
                        </StyledCustomIcon>
                      }
                      position="top"
                      parentTitle={true}
                      anchorElement="target"
                      openDelay={100}
                    />
                  </ImportButtonGroup>
                  <ButtonDivider />
                  <AddButton
                    text={`+ ${t(BUTTONS.CREATE_USER)}`}
                    textColor="white"
                    onClick={() => setIsCreateModalVisible(true)}
                  />
                </>
              )}
              <ReloadButton onClick={handleReloadClick}>
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <img
                    className="refresh"
                    height={15}
                    src={icons.refreshIcon.src}
                    srcSet={icons.refreshIcon.srcset}
                  />
                )}
              </ReloadButton>
            </HeaderActions>
          </HeaderBox>

          <FilterContainer>
            <FilterGroup>
              <FilterLabel>{t(FORM.FILTER)}:</FilterLabel>
              <Dropdown
                style={{ width: '250px' }}
                options={dropDownOptions}
                id="user-filter-dropdown"
                selectedOption={activeUsersFilter}
                onChangeOption={handleDropdownChange}
              />
            </FilterGroup>

            <SearchInput
              placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
              onChange={handleSearchChange}
            />
          </FilterContainer>

          <TableWrapper>
            <KGrid
              loading={isLoading}
              datasource={currentData.data}
              dataState={dataState}
              pageable={true}
              total={currentData.totalCount || 0}
              filterable={false}
              editMode={false}
              sortable={true}
              onFilterChange={handleFilterChange}
              onDataStateChange={onDataStateChange}
              style={{ height: '100%' }}
            >
              <GridColumn field="fullname" title={t(TABLE.USER_FULLNAME)} />
              <GridColumn field="email" title={t(TABLE.USER_EMAIL)} />
              <GridColumn
                sortable={false}
                field="roles"
                cells={{
                  data: (props) => <RolesCell {...props} />,
                }}
                title={t(ROLES.USER_ROLES)}
              />
              <GridColumn
                filterable={true}
                sortable={false}
                field="isDeleted"
                filter="boolean"
                cell={({ dataItem }) => (
                  <td>
                    {dataItem.isDeleted ? (
                      <CustomChipLable
                        label={t(TABLE.INACTIVE)}
                        variant="danger"
                      />
                    ) : (
                      <CustomChipLable
                        label={t(TABLE.ACTIVE)}
                        variant="success"
                      />
                    )}
                  </td>
                )}
                title={t(TABLE.USER_STATUS)}
              />
              <GridColumn
                field="actions"
                sortable={false}
                title={t(TABLE.USER_ACTIONS)}
                cell={({ dataIndex, dataItem }) => (
                  <ActionCell dataIndex={dataIndex % 10} dataItem={dataItem} />
                )}
              />
            </KGrid>
          </TableWrapper>
        </ContentContainer>

        {isCreateModalVisible && (
          <CreateUserModal onCancel={handleCreateModalClose} onClose={handleCreateModalClose} />
        )}

        {isInfoModalVisible && (
          <UserInfoModal 
            userId={userId} 
            onClose={handleInfoModalClose} 
            fetchUsers={fetchUsers}
            initialTabIndex={initialTabIndex}
          />
        )}

        {Modal}
      </Wrapper>
    </Container>
  )
}

export default AdminUserManagement

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const RolesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  cursor: default;
`

const RoleTag = styled.div`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  background: #e2e2e2;
  margin: 1px;
  font-size: 12px;
  cursor: default;
  font-weight: 500;
`

const StyledCustomIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e8f5e9;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ContentContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  flex: 1;
`

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e6e6e6;
`

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: #333;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const AddButton = styled(CustomButton)`
  background-color: #1e4ab7;
  border-radius: 4px;
  font-weight: 500;
  padding: 8px 16px;
  height: 30.5px;
`

const ReloadButton = styled.button`
  width: 30px;
  height: 30.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
  background-color: #f9f9f9;
  justify-content: space-between;
`

const FilterLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  width: 200px;
  font-size: 14px;
  
  &::placeholder {
    color: #888;
  }
  
  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
`

const LoadingSpinner = styled.div`
  width: 15px;
  height: 15px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const ImportButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  border-radius: 4px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #d0d0d0;
  }
`

const ButtonDivider = styled.div`
  width: 1px;
  height: 30px;
  background-color: #e0e0e0;
  margin: 0 8px;
`
